/**
 * Event constants transcribed from the Committee proposal deck
 * (SDG_Cultural_Day_2026_WebApp_Proposal_10.pptx).
 * Slide references are given so any change can be traced back to approval.
 */

export const EVENT = {
  name: 'Lincoln International SDG & Cultural Day 2026',
  tagline: '99 Countries, One Experience',
  dateISO: '2026-10-15', // Thursday, 15 October 2026 — slide 3
  dateLabel: 'Thursday, 15 October 2026',
  /** Without the weekday, for the stage screen's footer. */
  dateShort: '15 October 2026',
  organiser: 'SDG & Cultural Committee, Lincoln University College Malaysia',
  host: 'Lincoln University College Malaysia',
  countries: 99,
  technologyPartner: 'Techvision Softwares Sdn. Bhd.',
  technologyPartnerUrl: 'https://www.techvisionsoft.com',
} as const

/**
 * How long the big screen is busy announcing something.
 *
 * Shared because two things have to agree on it: the screen, which runs the
 * animation, and the server, which refuses a second announcement while the
 * first is still on the projector. If these drifted apart, Admin could start
 * a draw while the previous winner was still being revealed and the hall
 * would see the name change mid-sentence.
 */
export const STAGE = {
  /**
   * Lucky draw: 5s of counting, 7s of wheel, then the wheel holds on its
   * landing for two seconds before the card takes over. The card used to
   * replace the wheel the instant it stopped, which gave the hall no moment
   * to see where it had landed.
   */
  /**
   * Ten seconds, for every draw.
   *
   * The Grand Draw alone counted for ten and the other two for five, on the
   * reasoning that five winners apiece made a longer count too expensive.
   * The Committee asked for ten throughout: a draw that is over before the
   * room has quieted is a draw nobody watched, and the count is the part the
   * compère talks over. It is also what the award reveals use, so the whole
   * programme now has one rhythm instead of two.
   */
  drawCountdownMs: 10_000,
  /**
   * Seven seconds of wheel, at the Committee's request — it was five.
   *
   * Everything downstream reads this rather than a constant of its own: the
   * hand-off to the winner card, the fanfare on the landing, the name
   * re-dealing on the segments, and the server's refusal to start a second
   * announcement while this one is playing. Changing it here moves all of
   * them together.
   */
  drawSpinMs: 7000,
  drawHoldMs: 2000,
  /**
   * Award ceremony: the "now revealing" curtain. Longer than the draw's,
   * because the compère names the award and the place over it before the
   * card opens.
   */
  revealCountdownMs: 10_000,
} as const

/**
 * The whole drum-roll for an ordinary draw, end to end.
 *
 * Prefer `drawSequenceMs(drawNo)` — the Grand Draw counts for longer, and
 * anything that assumes this constant will release the stage guard five
 * seconds early on draw 3. Kept because it is still the right number for
 * draws 1 and 2 and reads better than the function where the draw is known
 * to be an ordinary one.
 */
export const DRAW_SEQUENCE_MS =
  STAGE.drawCountdownMs + STAGE.drawSpinMs + STAGE.drawHoldMs

/**
 * A little longer than the animation, so the screen has visibly finished
 * before the next button does anything. Covers the gap between the server
 * writing a row and the projector receiving it.
 */
export const STAGE_SETTLE_MS = 700

/** Slide 7, 12, 14 — the rules that drive student progress. */
export const RULES = {
  totalBooths: 14,
  /** Visiting this many booths enters the student into the lucky draw. */
  luckyDrawThreshold: 7,
  /**
   * Booths needed for the Certificate of Participation — **all 14**.
   *
   * As the deck has it. It was briefly lowered to 12, on the reasoning that a
   * student who found two booths closed should not be left with nothing; the
   * Committee put it back, because the certificate is for completing the
   * trail and a partial one devalues every other.
   *
   * Every count on the certificate, the pass page and the e-mails reads from
   * here, so this is the only place it is written down.
   */
  certificateThreshold: 14,
  otp: {
    length: 6,
    ttlMinutes: 10,
    resendSeconds: 60,
    maxAttempts: 5,
  },
  /** A student may win only once across all three draws — slide 14. */
  oneWinPerStudent: true,
  /** Winner not present after this many calls → re-draw, recorded in the log. */
  redrawAfterCalls: 3,
} as const

/**
 * Authentication model.
 *
 * Committee change to the deck: the proposal described per-booth links with
 * 4-digit PINs (slides 4, 11). Staff now use username + password instead, and
 * all booth / judge accounts are created by Admin. Students remain
 * password-free — registering issues a 1-day session.
 */
export const AUTH = {
  /** Students never set a password; the OTP proves the e-mail. */
  studentSessionHours: 24,
  /** Booth managers stay signed in for the whole event day. */
  staffSessionHours: 24,
  passwordMinLength: 8,
  /**
   * Whether an issued password is a temporary one the holder must replace.
   *
   * **Off**: the event is one day. The password a booth manager is e-mailed is
   * the password they use, and a forced change is a second thing to do on a
   * phone, at a booth, in a hall, before they can scan anybody — and a second
   * password to forget between opening the mail and arriving. They may still
   * change it whenever they like; they are simply not made to.
   */
  forcePasswordChangeOnSeed: false,
} as const

/**
 * 'mc' is the compère at the microphone — a read-only seat.
 *
 * Added because the stage screen is the wrong place to read a name from: it
 * shows the winner at the same instant as the hall, from an angle, in a font
 * sized for the back of the room. The MC was mispronouncing names because
 * they were seeing them for the first time while speaking.
 *
 * The console gives them the name **before** the projector does, and nothing
 * else — no buttons, nothing that changes what the audience sees.
 */
export type UserRole = 'admin' | 'booth_manager' | 'judge' | 'mc'
export type SessionRole = UserRole | 'student'

/**
 * Short registration ID.
 *
 * Every student who completes registration gets a compact, human-sized code —
 * A001…A100, then B001…B100, on to Z100. Short enough to read off a phone and
 * type into a booth scanner when a camera fails, and to quote at the help desk,
 * which a full Student ID like LUC-2291034 is not.
 */
export const REG_ID = {
  perLetter: 100,
  letters: 26,
  /** A001 … Z100 = 2,600 codes, against roughly 1,250 expected students. */
  capacity: 26 * 100,
} as const

/**
 * Turns a 1-based allocation number into its code.
 *   1 -> A001, 100 -> A100, 101 -> B001, 2600 -> Z100
 *
 * Past Z100 it rolls on to two letters (AA001…) rather than failing. Running
 * out of IDs must never be the thing that stops a student registering on the
 * day, and the format stays sortable and unambiguous.
 */
export function formatRegNo(sequence: number): string {
  if (sequence < 1) throw new Error(`formatRegNo: sequence must be >= 1, got ${sequence}`)

  const index = sequence - 1
  const letterIndex = Math.floor(index / REG_ID.perLetter)
  const number = (index % REG_ID.perLetter) + 1

  let prefix = ''
  if (letterIndex < REG_ID.letters) {
    prefix = String.fromCharCode(65 + letterIndex)
  } else {
    // Second cycle: AA, AB, AC … keeping codes unique beyond 2,600.
    const overflow = letterIndex - REG_ID.letters
    prefix =
      String.fromCharCode(65 + Math.floor(overflow / REG_ID.letters)) +
      String.fromCharCode(65 + (overflow % REG_ID.letters))
  }

  return `${prefix}${String(number).padStart(3, '0')}`
}

/** Accepts "a001", " A001 " etc. and returns the canonical "A001". */
export function normaliseRegNo(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '')
}

/** True for a well-formed code: one or two letters then three digits. */
export function isRegNo(value: string): boolean {
  return /^[A-Z]{1,2}\d{3}$/.test(normaliseRegNo(value))
}

// ── Certificate numbering — slide 12 ──────────────────────────

/**
 * The deck shows "Cert. No. LISCD26-000412": Lincoln International SDG &
 * Cultural Day, year 26, then a zero-padded serial.
 */
export const CERT = {
  prefix: 'LISCD26',
  digits: 6,
  /** Slide 12; the deck notes the final signatory is the Committee's to confirm. */
  signatory: 'Chairperson, SDG & Cultural Committee',

  /**
   * The one line of wording the app supplies, and only on the **award**
   * certificate (`shared/certificate-template.pdf`).
   *
   * Everything else on either certificate — the title, "This certificate is
   * proudly presented to", the recognition line, the event name, the tagline
   * and the date — is printed in the Committee's own template, so it must not
   * be drawn again here. The participation certificate every student receives
   * has no such blank at all: its recognition line runs straight into the
   * event name, which is why this is not used for it.
   *
   * The award template's fixed lead-in ends "as the recipient of", which
   * forces this to be the *name of a recognition* rather than a description:
   * "ALL 14 BOOTHS" does not follow "recipient of". The count comes from the
   * rules, so moving the threshold moves the certificate with it.
   */
  achievement: `FULL PARTICIPATION AWARD · ALL ${RULES.totalBooths} BOOTHS`,
} as const

/**
 * The four UN goals the event is aligned to, as the approved certificate
 * prints them — number, title and the goal's own colour.
 *
 * On the certificate they appear twice: as the four-colour bar under the
 * achievement, and as the row of tiles above the date.
 */
export const CERT_GOALS = [
  { goal: 3, title: 'Good health and well-being', colour: '4C9F38' },
  { goal: 4, title: 'Quality education', colour: 'C5192D' },
  { goal: 11, title: 'Sustainable cities and communities', colour: 'FD9D24' },
  { goal: 13, title: 'Climate action', colour: '3F7E44' },
] as const

/** 412 -> "LISCD26-000412". */
export function formatCertNo(sequence: number): string {
  if (sequence < 1) throw new Error(`formatCertNo: sequence must be >= 1, got ${sequence}`)
  return `${CERT.prefix}-${String(sequence).padStart(CERT.digits, '0')}`
}

/** Accepts "liscd26-000412", " LISCD26-000412 " etc. */
export function normaliseCertNo(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '')
}

export function isCertNo(value: string): boolean {
  return new RegExp(`^${CERT.prefix}-\\d{${CERT.digits},}$`).test(normaliseCertNo(value))
}

/**
 * The 14 school booths, verbatim from slide 6. `seq` is the deck's "NO." column.
 *
 * `manager` is the booth manager named on the Committee's roster, and their
 * **work e-mail is their login user id** — not a `booth07`-style handle. The
 * managers are academics who will each sign in once, on their own phone, at a
 * booth, in a hall, on the day; an address they already know beats a handle
 * they would have to be told. It also makes the account traceable to a person
 * rather than to a position.
 *
 * Stored lower-case because sign-in lower-cases what is typed before matching.
 */
export const BOOTHS = [
  { seq: 1, boothNo: 6, school: 'School of Medicine', manager: { name: 'Dr. Dwe San', email: 'dwesan85@lincoln.edu.my' } },
  { seq: 2, boothNo: 1, school: 'Faculty of Nursing', manager: { name: 'Chaimae / Ms. Zanna', email: 'chaimae@lincoln.edu.my' } },
  { seq: 3, boothNo: 12, school: 'Faculty of Applied Science', manager: { name: 'Dr. Syamimi', email: 'syamimi@lincoln.edu.my' } },
  { seq: 4, boothNo: 13, school: 'School of Dentistry', manager: { name: 'Madam Mahani', email: 'mahani@lincoln.edu.my' } },
  { seq: 5, boothNo: 5, school: 'School of Pharmacy', manager: { name: 'Ms. Adilah', email: 'adilahamran@lincoln.edu.my' } },
  { seq: 6, boothNo: 3, school: 'School of Engineering', manager: { name: 'Puan Jasmine', email: 'jasminemg@lincoln.edu.my' } },
  { seq: 7, boothNo: 7, school: 'School of Computer Science and Information Technology', manager: { name: 'Prof. Dr. Midhun', email: 'midhun@lincoln.edu.my' } },
  { seq: 8, boothNo: 10, school: 'School of Business & Accounting', manager: { name: 'Dayangku Azriani', email: 'dayangkuazriani@lincoln.edu.my' } },
  { seq: 9, boothNo: 9, school: 'School of Hospitality and Tourism', manager: { name: 'Ms. Intan', email: 'intanbahar@lincoln.edu.my' } },
  { seq: 10, boothNo: 2, school: 'School of Social Sciences & Humanity', manager: { name: 'Mr. Manimaren', email: 'manimaren@lincoln.edu.my' } },
  { seq: 11, boothNo: 4, school: 'School of Education', manager: { name: 'Ms. Marini Mazlan', email: 'marini@lincoln.edu.my' } },
  { seq: 12, boothNo: 8, school: 'Centre of Foundation Studies', manager: { name: 'Ms. Supriyati Zainuddin', email: 'supriyatiz@lincoln.edu.my' } },
  { seq: 13, boothNo: 11, school: 'English and General Studies', manager: { name: 'Mr. Dhamir Audi', email: 'audiazizul@lincoln.edu.my' } },
  { seq: 14, boothNo: 14, school: 'School of Performing Arts & Design', manager: { name: 'Mr. Zhakri', email: 'zhakri@lincoln.edu.my' } },
] as const

/**
 * The 16 entrepreneurship booths — the Committee's "ENT" floor plan, E1 to E16.
 *
 * A late Committee addition, and deliberately **not** part of `BOOTHS`. These
 * are a different kind of thing, and three rules follow from that:
 *
 *   - **No manager account.** Nobody signs in for them, so no credentials are
 *     issued and none of them appear in the staff account list.
 *   - **No scanning.** They are not on the student's booth trail, so visiting
 *     one does not count toward the 12 needed for a certificate, does not move
 *     anyone toward the lucky draw, and does not feed Most Active School.
 *   - **Judged, and only judged.** The three judges mark each one against the
 *     entrepreneurship rubric below. That is the entire extent of their
 *     presence in this system.
 *
 * Putting them in `booths` would have given all three of those away silently:
 * every query that counts booths, issues logins or totals scans walks that
 * table, and 16 new rows would have joined all of them without a line changing.
 *
 * `code` is the booth number painted on the floor — E1, not 1 — which is why
 * it is a string and why `booths.boothNo` could not hold it.
 */
export const ENT_BOOTHS = [
  { seq: 1, code: 'E1', name: 'Faculty of Nursing (F&B)' },
  { seq: 2, code: 'E2', name: 'Faculty of Applied Science' },
  { seq: 3, code: 'E3', name: 'School of Pharmacy (Banana Chips)' },
  { seq: 4, code: 'E4', name: 'School of Hospitality and Tourism (F&B)' },
  { seq: 5, code: 'E5', name: 'School of Social Sciences & Humanity' },
  { seq: 6, code: 'E6', name: 'Centre of Foundation Studies (F&B)' },
  { seq: 7, code: 'E7', name: 'Cultural Booth: Myanmar' },
  { seq: 8, code: 'E8', name: 'Cultural Booth: Yemen' },
  { seq: 9, code: 'E9', name: 'School of Medicine (F&B)' },
  { seq: 10, code: 'E10', name: 'Entrepreneurship: Pakistan' },
  { seq: 11, code: 'E11', name: 'Entrepreneurship: Nepal (F&B)' },
  { seq: 12, code: 'E12', name: 'CFS (S) (F&B)' },
  { seq: 13, code: 'E13', name: 'Cultural Booth: Sudan' },
  { seq: 14, code: 'E14', name: 'Cultural Booth: Syria' },
  { seq: 15, code: 'E15', name: 'Entrepreneurship: Uzbekistan' },
  { seq: 16, code: 'E16', name: 'Cultural Booth: Chinese' },
] as const

export const ENT_BOOTH_COUNT = ENT_BOOTHS.length

/** Registration drop-down — the same 14 schools, in the deck's listed order. */
export const SCHOOLS: readonly string[] = BOOTHS.map((b) => b.school)

/** Validates a submitted school against the drop-down (slide 8: required). */
export function isKnownSchool(value: string): boolean {
  return SCHOOLS.includes(value)
}

/**
 * Attendees from other institutions.
 *
 * The deck assumed every attendee was a Lincoln student (slide 8), but the
 * cultural day draws students from other colleges too. They type the name of
 * their institution instead of picking one of the 14 schools, and they have no
 * Lincoln Student ID at all — see `students.isLincolnStudent`.
 */
export const INSTITUTION = {
  minLength: 2,
  /**
   * 60 characters — a hard limit, and a deliberately tight one.
   *
   * This was 160 (the width of `students.school`) and that was a mistake. The
   * name typed here is printed in places with no room to argue: a pill on the
   * student's pass, one line of a certificate, and the winner's card on a
   * 3-metre projector where it sits beside their name in 2.2vmin type. A
   * 160-character entry does not merely look untidy in those places — it
   * pushes the layout apart.
   *
   * 60 is the cap, not the column: the longest of the 14 Lincoln schools is
   * "School of Computer Science and Information Technology" at 53, so one
   * limit covers both kinds of attendee and every display is sized for a
   * string that already exists in the data.
   */
  maxLength: 60,
} as const

export function isValidInstitution(value: string): boolean {
  const trimmed = value.trim()
  return trimmed.length >= INSTITUTION.minLength && trimmed.length <= INSTITUTION.maxLength
}

/**
 * The identifier to print beside a student's name.
 *
 * Their Lincoln Student ID when they have one, otherwise their registration
 * code — which every attendee gets once their e-mail is verified. Returns
 * null only before that code is allocated, i.e. for an unfinished sign-up.
 *
 * Centralised because it is read in eight places — the pass, the certificate,
 * two e-mails, the draw screen, the booth scanner, the admin register and the
 * CSV exports — and "whichever of these two is set" is exactly the kind of
 * rule that gets written seven ways and forgotten in the eighth.
 */
export function studentRef(student: {
  studentId?: string | null
  regNo?: string | null
}): string | null {
  return student.studentId ?? student.regNo ?? null
}

export type RubricCode =
  | 'sdg_project'
  | 'sustainable_booth'
  | 'cultural_performance'
  | 'entrepreneurship'

/**
 * What a rubric is marked against.
 *
 * `booth` is one of the 14 school booths; `ent_booth` one of the 16
 * entrepreneurship booths, which are judged but never scanned (see
 * `ENT_BOOTHS`); `performance_group` one of the groups on stage.
 */
export type SubjectType = 'booth' | 'ent_booth' | 'performance_group'

export interface RubricKpi {
  seq: number
  label: string
  weight: number
}

export interface Rubric {
  code: RubricCode
  title: string
  description: string
  subjectType: SubjectType
  /** True when winners come from judges' scores rather than Admin entry. */
  judged: true
  kpis: RubricKpi[]
}

/** Slides 19, 20, 21 — every KPI and weight, verbatim. Each set sums to 100. */
export const RUBRICS: Rubric[] = [
  {
    code: 'sdg_project',
    title: 'Best SDG Project',
    description:
      'Recognises the booth whose project best addresses one or more UN Sustainable Development Goals.',
    subjectType: 'booth',
    judged: true,
    kpis: [
      { seq: 1, label: 'Relevance to the selected SDG(s)', weight: 20 },
      { seq: 2, label: 'Innovation and creativity', weight: 20 },
      { seq: 3, label: 'Practicality and feasibility', weight: 15 },
      { seq: 4, label: 'Environmental or social impact', weight: 20 },
      { seq: 5, label: 'Research quality and understanding', weight: 10 },
      { seq: 6, label: 'Presentation and audience engagement', weight: 10 },
      { seq: 7, label: 'Potential for continuity or scalability', weight: 5 },
    ],
  },
  {
    code: 'sustainable_booth',
    title: 'Best Sustainable Booth Practice',
    description:
      'Recognises the booth that best practises sustainability in how it is built and run on the day.',
    /*
     * The 16 entrepreneurship booths, not the 14 school ones.
     *
     * The Committee split the two booth awards between the two floors rather
     * than having both panels judge all thirty: Best SDG Project is for the
     * school booths, this one for E1 to E16. Each judge marks a booth once,
     * for one award, which is also what keeps the sheet count workable on a
     * single day.
     */
    subjectType: 'ent_booth',
    judged: true,
    kpis: [
      { seq: 1, label: 'Use of reusable, recyclable or biodegradable materials', weight: 20 },
      { seq: 2, label: 'Waste segregation and reduction practices', weight: 20 },
      { seq: 3, label: 'Energy, water and resource efficiency', weight: 15 },
      { seq: 4, label: 'Sustainable products or activities presented', weight: 15 },
      { seq: 5, label: 'Cleanliness, safety and booth organization', weight: 10 },
      { seq: 6, label: 'Visitor education and engagement', weight: 10 },
      { seq: 7, label: 'Creativity and overall booth presentation', weight: 10 },
    ],
  },
  {
    code: 'cultural_performance',
    title: 'Best Cultural Performance',
    description:
      'Stage award: the 3 judges score each performance group right after it performs.',
    subjectType: 'performance_group',
    judged: true,
    kpis: [
      { seq: 1, label: 'Cultural authenticity and representation', weight: 25 },
      { seq: 2, label: 'Performance quality and technical execution', weight: 20 },
      { seq: 3, label: 'Creativity and originality', weight: 15 },
      { seq: 4, label: 'Coordination and teamwork', weight: 15 },
      { seq: 5, label: 'Stage presence and audience engagement', weight: 15 },
      { seq: 6, label: 'Costume, music and supporting elements', weight: 5 },
      { seq: 7, label: 'Compliance with the allocated performance time', weight: 5 },
    ],
  },
]

/**
 * How many groups take the stage — **7**.
 *
 * Slide 18 says 5; the Committee added two more. Every count derived from it
 * moves together: the judges' sheets, the expected total that gates the
 * ceremony, and the stage award's subject list.
 */
export const PERFORMANCE_GROUP_COUNT = 7

/**
 * Slide 18 — 3 judges on the panel, named by the Committee's roster.
 *
 * Each judge's **work e-mail is their login user id**, as for the booth
 * managers. Their `label` is *not* their name: "Judge 1".."Judge 3" head the
 * columns of the scoring matrix on slide 23, order the exports, and are what
 * the other judges see on the tie-break panel. Scoring stays attributable but
 * not personal — a judge seeing "Dr. Farra ranked this third" is a different
 * conversation from one seeing "Judge 1 ranked this third".
 *
 * Their own console greets them by name: it is the one screen where they are
 * the only reader.
 */
export const JUDGES = [
  { no: 1, name: 'Dr. Farra', email: 'farraaidah@lincoln.edu.my' },
  { no: 2, name: 'Dr. Dhurga', email: 'dhurgadevi@lincoln.edu.my' },
  { no: 3, name: 'Mr. Azzeme', email: 'azzeme@lincoln.edu.my' },
] as const

/** The matrix column header, and the judges table's stable unique key. */
export function judgeLabel(no: number): string {
  return `Judge ${no}`
}

export const JUDGE_COUNT = JUDGES.length

/**
 * How many subjects each kind of rubric is marked against.
 *
 * One kind of subject per rubric: Best SDG Project is marked against the 14
 * school booths, Best Sustainable Booth Practice against the 16
 * entrepreneurship booths, and Best Cultural Performance against the 5 stage
 * groups. The Committee split the two booth awards across the two floors
 * rather than giving the ENT booths an award of their own.
 */
export const SUBJECT_COUNTS: Record<SubjectType, number> = {
  booth: BOOTHS.length,
  ent_booth: ENT_BOOTH_COUNT,
  performance_group: PERFORMANCE_GROUP_COUNT,
}

/**
 * Every digital score sheet the panel owes — 195 of them:
 * 3 judges × (14 school booths + 16 ENT booths + 7 performance groups)
 * = 3 × 65 = 195.
 *
 * Slide 18 says 99, which counted only the 14 school booths. The 16
 * entrepreneurship booths are judged on the same two booth rubrics, so each
 * judge now marks 30 booths twice over rather than 14.
 *
 * Derived from `RUBRICS` rather than written down, so a rubric added or a
 * booth withdrawn moves this number with it — slide 24 holds the whole
 * ceremony back until this many sheets are in, and a stale constant here
 * would either publish early or never publish at all.
 */
export const EXPECTED_SCORE_SHEETS =
  JUDGE_COUNT * RUBRICS.reduce((n, r) => n + SUBJECT_COUNTS[r.subjectType], 0)

/**
 * The form a student fills in to claim their Certificate of Participation.
 *
 * The Committee's change to slide 13: the certificate used to be e-mailed the
 * moment the booth trail was complete. It is now *earned and then claimed* —
 * the student is told it is ready, and collects it by sending back a photo
 * from the day and a rating of the event.
 *
 * The point of it is the photographs and the rating: an event that hands out
 * 1,250 certificates and learns nothing has missed the one moment when every
 * attendee is motivated to reply.
 *
 * **One photo is required, five is the ceiling.** One, because a claim with
 * none is just a download button; five, because the uploads are kept on the
 * event server and 1,250 students × 5 photos at 5 MB is already 30 GB if
 * everyone maxes it out.
 */
export const CERT_CLAIM = {
  minPhotos: 1,
  maxPhotos: 5,
  /** Per photo. Phone cameras produce 2–4 MB; 6 leaves room without inviting video. */
  maxPhotoBytes: 6 * 1024 * 1024,
  /** What a phone's camera roll actually offers, including iPhone's HEIC. */
  photoTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
  minRating: 1,
  maxRating: 5,
  commentMaxLength: 300,
  socialUrlMaxLength: 400,
} as const

export function isValidRating(value: number): boolean {
  return Number.isInteger(value) && value >= CERT_CLAIM.minRating && value <= CERT_CLAIM.maxRating
}

/** Accepts a http(s) link, or nothing at all — the field is optional. */
export function isValidSocialUrl(value: string): boolean {
  const v = value.trim()
  if (v.length === 0) return true
  if (v.length > CERT_CLAIM.socialUrlMaxLength) return false
  return /^https?:\/\/[^\s]+\.[^\s]+$/i.test(v)
}

/** Slide 14 — three draws, eleven winners. */
export const LUCKY_DRAWS = [
  { drawNo: 1, name: 'Lucky Draw 1', winnersTarget: 5, theme: 'navy' },
  { drawNo: 2, name: 'Lucky Draw 2', winnersTarget: 5, theme: 'purple' },
  { drawNo: 3, name: 'Grand Lucky Draw', winnersTarget: 1, theme: 'gold' },
] as const

/**
 * The Grand Lucky Draw — the last one, and the only single-winner draw.
 *
 * Derived from the list rather than written as `3`, so adding a draw does not
 * leave this pointing at the wrong one.
 */
export const GRAND_DRAW_NO = LUCKY_DRAWS[LUCKY_DRAWS.length - 1].drawNo

/**
 * How long this draw counts down before the wheel starts.
 *
 * Three things have to agree on this number: the wheel that animates it, the
 * screen's hand-off from wheel to winner card, and the server's refusal to
 * start a second announcement while this one is playing. They read it from
 * here for exactly that reason — if they drifted apart, Admin could start a
 * draw mid-spin and the hall would watch the name change under the pointer.
 *
 * Every draw now counts for the same ten seconds, so `drawNo` changes
 * nothing. The parameter stays because the three callers are per-draw and
 * this is where a draw would differ again if the Committee asked.
 */
export function drawCountdownMs(_drawNo: number): number {
  return STAGE.drawCountdownMs
}

/** The whole drum-roll for one draw: count, spin, then hold on the landing. */
export function drawSequenceMs(drawNo: number): number {
  return drawCountdownMs(drawNo) + STAGE.drawSpinMs + STAGE.drawHoldMs
}

/**
 * When the wheel stops turning — the count plus the spin, without the hold.
 *
 * The two differ by `drawHoldMs`, and that gap is where the winner's fanfare
 * belongs: the wheel lands, the fanfare starts immediately, and the card
 * takes over two seconds later while it is still playing. Firing the fanfare
 * at the *end* of the sequence instead left two seconds of silence between
 * the wheel stopping and anything happening.
 */
export function drawLandsMs(drawNo: number): number {
  return drawCountdownMs(drawNo) + STAGE.drawSpinMs
}

/**
 * Most Active Club — six clubs, all of them first, named by the Committee.
 *
 * A fixed roster rather than something Admin types on the day, like `BOOTHS`
 * and `JUDGES`. The Committee decided these six in advance, so a console form
 * for them only offered a way to misspell a club's name onto a 3-metre screen
 * minutes before it is read out.
 *
 * `no` is the order they are announced in, **not** a rank. The clubs share
 * the award equally; there is no second and no third, and the screen gives
 * them identical cards for that reason. They are revealed one at a time only
 * so the compère can name each in turn.
 */
export const CLUBS = [
  { no: 1, name: 'FICAR FISCO (under CFS)' },
  { no: 2, name: 'Pertubuhan Seni Gayung Fatani LUC' },
  { no: 3, name: 'Lincoln Indian Society club' },
  { no: 4, name: 'Sudanese Students Society' },
  { no: 5, name: 'Mindfuel Club (under Psychology)' },
  { no: 6, name: 'Namaste Nepal LUC' },
] as const

export const CLUB_AWARD = {
  count: CLUBS.length,
  nameMaxLength: 60,
} as const

/**
 * How many schools take the Most Active School award — **2**.
 *
 * Read by the ceremony entry below, by `publishActiveBooths` when it freezes
 * the standings, and by `contestedPlaces`, which is what makes Admin settle a
 * tie that touches the award rather than letting the count pick one.
 */
export const MOST_ACTIVE_PLACES = 2

/** Slide 25 — 4 awards, 9 winners on stage. */
export const AWARD_CEREMONY = [
  { code: 'sdg_project', title: 'Best SDG Project', places: 3, source: 'judges', screen: 'podium' },
  {
    code: 'sustainable_booth',
    title: 'Best Sustainable Booth Practice',
    places: 3,
    source: 'judges',
    screen: 'podium',
  },
  {
    code: 'cultural_performance',
    title: 'Best Cultural Performance',
    places: 1,
    source: 'judges',
    screen: 'spotlight',
  },
  {
    code: 'traditional_attire',
    title: 'Best Traditional Attire',
    places: 2, // Male + Female — entered by Admin, slide 27
    source: 'admin',
    screen: 'twin',
  },
  /*
   * Committee addition to the deck's four awards.
   *
   * Every other award is somebody's opinion — three judges marking a rubric,
   * or Admin picking an outfit. This one is not: it is the booth attendance
   * already in the database, counted. The school whose booth drew the most
   * students wins it, and the number is checkable by anyone who was there.
   *
   * `source: 'scans'` rather than 'judges', which matters for when it can be
   * published: slide 24 holds the judged awards back until every score
   * sheet is in, and this one has nothing to do with them. It can be
   * published the moment the floor closes.
   */
  {
    code: 'most_active_booth',
    title: 'Most Active School',
    /*
     * Two schools, not a podium of three.
     *
     * It was one. The Committee widened it to two so a second school is
     * recognised, but stopped short of a third: this is "which booth drew the
     * most students", and by this point in the ceremony the hall has already
     * watched two 3rd/2nd/1st reveals. Two is a pair, which reads differently
     * from a podium.
     */
    places: MOST_ACTIVE_PLACES,
    source: 'scans',
    /** Its own stage: the count rolls up rather than a card opening. */
    screen: 'active',
  },
  /*
   * Most Active Club. Also a Committee addition, and the mirror image of the
   * one above: that award is counted and this one cannot be.
   *
   * Clubs are not in the system at all — no table, no membership, no scans —
   * so there is nothing to rank. The Committee decides it the way they decide
   * Best Traditional Attire, by typing the winner in.
   */
  {
    code: 'most_active_club',
    title: 'Most Active Clubs',
    /*
     * Six announcements, not six positions. `places` counts the steps the
     * ceremony takes, and the Committee wants each club named in turn rather
     * than all six appearing together — but they share the award, so nothing
     * on the screen calls one of them first.
     */
    places: CLUBS.length,
    source: 'admin',
    /** Its own stage: a crest, then each club's name as it is called. */
    screen: 'club',
  },
] as const

/**
 * Most Active Club — the field lengths, and why they are short.
 *
 * Typed by Admin rather than counted, because clubs are not in the database:
 * there is no club table, no membership and nothing to count. The Committee
 * knows which one it is and types the name.
 *
 * The caps are deliberately tight for the reason the institution field's are:
 * every one of these strings is printed on a 3-metre projector, and a long
 * one does not merely look untidy there, it pushes the layout apart. 60 is
 * the same limit the registration form uses, and the citation gets 120 — one
 * line of large type, not a paragraph.
 */
/**
 * Most Active Club — six clubs, all of them first.
 *
 * Not a podium: the Committee names six clubs and they share the award
 * equally, so there is no second and no third. Names only — no school, no
 * citation — because six entries have to fit one screen legibly, and six
 * three-line cards do not.
 */

/**
 * Slide 22 — a judge's mark on one KPI becomes a weighted score.
 * KPI score = (mark ÷ 10) × weight. A full sheet totals 100.
 */
export function kpiScore(mark: number, weight: number): number {
  return (mark / 10) * weight
}

/** A judge's total for one entry, out of 100. */
export function sheetTotal(entries: { mark: number; weight: number }[]): number {
  return entries.reduce((sum, e) => sum + kpiScore(e.mark, e.weight), 0)
}

/** Final score = average of the 3 judges, rounded to 2 dp (slide 22: 80.17). */
export function finalScore(judgeTotals: number[]): number {
  if (judgeTotals.length === 0) return 0
  const avg = judgeTotals.reduce((a, b) => a + b, 0) / judgeTotals.length
  return Math.round(avg * 100) / 100
}

/**
 * How far apart two scores are pulled on screen when the judging panel had to
 * separate them — one hundredth of a point, the smallest step the two-decimal
 * score can take.
 */
const TIE_DISPLAY_STEP = 0.01

/**
 * The scores the big screen prints, given the published places.
 *
 * A tie the panel settled leaves two places holding the *same* number: 2nd on
 * 58.50 and 3rd on 58.50. That is the arithmetic truth — both booths really
 * did score 58.50, which is why the panel had to choose — but on a 3-metre
 * screen it reads as a fault in the software rather than as a decision, and a
 * hall full of students will say so.
 *
 * So the lower place is shown a hundredth of a point down, which is enough to
 * separate them and small enough that it does not change what anyone sees as
 * the result.
 *
 * **This is a display rule and nothing more.** The judges' real scores stay in
 * `award_results`, in the standings the Committee reads, in the CSV export and
 * in the audit trail; only the projector sees the adjusted figure. Returned as
 * a map rather than applied in place so the two can never be confused.
 */
export function displayScores(
  places: { place: number; finalScore: number | null }[],
): Map<number, number | null> {
  const out = new Map<number, number | null>()

  /** Places that share an identical score, keyed on that score. */
  const groups = new Map<number, { place: number }[]>()

  for (const entry of places) {
    out.set(entry.place, entry.finalScore)
    if (entry.finalScore == null) continue

    const list = groups.get(entry.finalScore) ?? []
    list.push({ place: entry.place })
    groups.set(entry.finalScore, list)
  }

  for (const [score, tied] of groups) {
    if (tied.length < 2) continue

    // Best place keeps the real score; each one below drops a hundredth.
    tied
      .sort((a, b) => a.place - b.place)
      .forEach((entry, i) => {
        out.set(entry.place, Math.round((score - i * TIE_DISPLAY_STEP) * 100) / 100)
      })
  }

  return out
}

/** Slide 18 — every KPI is marked on a 1–10 scale. */
export const MARK = {
  min: 1,
  max: 10,
  /** The scale's endpoints, as labelled on slide 18. */
  minLabel: 'Very poor',
  maxLabel: 'Outstanding work',
} as const

/**
 * The 1–10 ramp exactly as slide 18 paints it: red through amber and yellow
 * to green and the event's teal. Read out of the deck's DrawingML, including
 * which swatches need dark text to stay legible.
 */
export const MARK_SCALE = [
  { mark: 1, fill: '#C0392B', ink: '#FFFFFF' },
  { mark: 2, fill: '#D35400', ink: '#FFFFFF' },
  { mark: 3, fill: '#E67E22', ink: '#FFFFFF' },
  { mark: 4, fill: '#F39C12', ink: '#1F2937' },
  { mark: 5, fill: '#F1C40F', ink: '#1F2937' },
  { mark: 6, fill: '#C3D12E', ink: '#1F2937' },
  { mark: 7, fill: '#8BC34A', ink: '#FFFFFF' },
  { mark: 8, fill: '#4CAF50', ink: '#FFFFFF' },
  { mark: 9, fill: '#2E9E5B', ink: '#FFFFFF' },
  { mark: 10, fill: '#0F8B8D', ink: '#FFFFFF' },
] as const

/** The two end labels on slide 18, in their own colours. */
export const MARK_SCALE_ENDS = {
  low: { text: `${MARK.min} = ${MARK.minLabel}`, colour: '#C0392B' },
  high: { text: `${MARK.max} = ${MARK.maxLabel}`, colour: '#0F8B8D' },
} as const

export function isValidMark(value: number): boolean {
  return Number.isInteger(value) && value >= MARK.min && value <= MARK.max
}

/**
 * Ranks entries within one award category — slide 22.
 *
 * Three discriminators, in order:
 *
 *   1. the highest final score
 *   2. the combined score on the three highest-weighted KPIs
 *   3. the judging panel's own decision
 *
 * The deck proposes the first two and leaves the third as "decision of the
 * judging panel". That decision cannot be computed, but it *can* be
 * collected: each judge orders the tied entries in their own console and the
 * majority takes the place (see `panelRank`). A tie that survives all three
 * is still reported rather than broken — two booths cannot share a place on
 * the podium, but inventing the winner is not ours to do either.
 */
export interface RankableEntry {
  subjectKey: string
  finalScore: number
  /** Combined score across the rubric's three highest-weighted KPIs. */
  tieBreakScore: number
  /**
   * Where the panel put this entry inside its tie group — 1 is best.
   *
   * Null when no panel decision applies: either nothing is tied here, or the
   * judges have not voted yet. Only ever compared between entries that are
   * already level on both scores above, so it cannot reorder the table at
   * large.
   */
  panelRank?: number | null
}

export interface RankedEntry<T extends RankableEntry> {
  entry: T
  place: number
  /** True when this entry is still level with another after the tie-break. */
  unresolvedTie: boolean
}

export function rankEntries<T extends RankableEntry>(entries: T[]): RankedEntry<T>[] {
  /** Unranked sorts last, so an undecided tie never jumps a decided one. */
  const panel = (e: T) => e.panelRank ?? Number.POSITIVE_INFINITY

  const sorted = [...entries].sort(
    (a, b) =>
      b.finalScore - a.finalScore ||
      b.tieBreakScore - a.tieBreakScore ||
      panel(a) - panel(b),
  )

  return sorted.map((entry, i) => {
    const level = (other: T | undefined) =>
      other !== undefined &&
      other.finalScore === entry.finalScore &&
      other.tieBreakScore === entry.tieBreakScore &&
      // Two entries the panel has separated are not level, whatever their
      // scores say. Two it has not reached yet still are.
      panel(other) === panel(entry)

    /*
     * Standard competition ranking: entries that are still level share a
     * place, and the next distinct entry skips the places they used up.
     */
    let place = i + 1
    for (let j = i - 1; j >= 0 && level(sorted[j]); j--) place = j + 1

    return {
      entry,
      place,
      unresolvedTie: level(sorted[i - 1]) || level(sorted[i + 1]),
    }
  })
}

/** Slide 15 — the big screen shows a masked ID: LUC-23•••41. */
export function maskStudentId(studentId: string): string {
  if (studentId.length <= 6) return studentId
  return `${studentId.slice(0, 6)}•••${studentId.slice(-2)}`
}

/** Slide 9 — the OTP screen shows a masked address: a****h@gmail.com. */
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!domain || local.length < 2) return email
  return `${local[0]}${'*'.repeat(Math.max(1, local.length - 2))}${local.at(-1)}@${domain}`
}
